import { useAppSelector } from 'store/hooks';
import * as React from 'react';

import { Panel } from 'ui/Panel';
import { isArray } from 'lib/typeguards';
import { LocationTablesAndZonesCardEditComponent } from 'components/location/cards/LocationTablesAndZonesCardEditComponent';
import { locationApi } from 'components/location/LocationApi';
import { ILocationZones, Location } from 'components/location/models/LocationModel';
import { EPointOfSaleProvider } from 'components/settings/PointOfSaleSettings';
import { MESSAGE_TABLE_ZONE_UPDATE_ERROR, MESSAGE_TABLE_ZONE_UPDATE_SUCCESS } from 'config/messages';
import { useSnackbar } from 'notistack';
import { LocationTablesAndZonesCardComponent } from './LocationTablesAndZonesCardComponent';

export interface ILocationTablesAndZonesCardProps {
    currentLocation: Location;
    setCurrentLocation: (location: Location) => void;
}

export const LocationTablesAndZonesCard: React.FC<ILocationTablesAndZonesCardProps> = ({
    currentLocation,
    setCurrentLocation
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const { settings } = useAppSelector(state => state.settings);
    const posProvider = React.useMemo(() => settings?.posProvider, [settings]);

    const [isEditing, setEditingStatus] = React.useState(false);
    const [isLoading, setLoading] = React.useState(false);

    const toggleState = React.useCallback(() => {
        setEditingStatus(currentStatus => !currentStatus);
    }, [setEditingStatus]);

    const handleSubmit = React.useCallback(
        async (zones: ILocationZones) => {
            const locationId = currentLocation._id;

            setLoading(true);

            const newSchema: ILocationZones = {
                ...zones
            };

            Object.entries(zones).forEach(([key, value]) => {
                // Change to new schema
                if (isArray(value)) {
                    newSchema[key] = {
                        tables: value as unknown as [number, number][]
                    };
                }
            });

            const response = await locationApi.update(locationId, {
                body: {
                    zones: newSchema
                }
            });

            if (response.ok) {
                setEditingStatus(false);
                setCurrentLocation(response.body);
                enqueueSnackbar(MESSAGE_TABLE_ZONE_UPDATE_SUCCESS, { variant: 'success' });
            } else {
                enqueueSnackbar(
                    response.body?.httpCode === 422 ? response.body.message : MESSAGE_TABLE_ZONE_UPDATE_ERROR,
                    {
                        variant: 'error'
                    }
                );
            }

            setLoading(false);
        },
        [currentLocation._id, setCurrentLocation, enqueueSnackbar]
    );

    const isZonalOrComtrex = React.useMemo(
        () =>
            posProvider === EPointOfSaleProvider.COMTREX || posProvider === EPointOfSaleProvider.ZONAL_AZTEC,
        [posProvider]
    );

    return (
        <Panel title="Tables & Zones" showAction={!isEditing} divider onClick={toggleState}>
            {isEditing ? (
                <LocationTablesAndZonesCardEditComponent
                    zones={currentLocation.zones}
                    onClose={toggleState}
                    isLoading={isLoading}
                    onSubmit={handleSubmit}
                    isZonalOrComtrex={isZonalOrComtrex}
                />
            ) : (
                <LocationTablesAndZonesCardComponent zones={currentLocation.zones} />
            )}
        </Panel>
    );
};
